import {differenceInDays, subDays} from "date-fns";
import {useEffect, useRef, useState} from "react";
import ReportUtils from "../../../utils/report-utils.js";
import GarrisonListService from "../../../services/reports/garrison-list-service.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";
import {Template} from "../../../components/template/template.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import Filters from "./components/filters.jsx";
import {Card} from "../../../components/card/card.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Content from "./components/content.jsx";

const initialFilter = () => ({
    dtStart: subDays(new Date(), 3),
    dtEnd: new Date(),
    latLng: "",
    range: 10,
    leader: [],
    guard: [],
    team: [],
    vehicle: [],
});

const filterLabels = {
    dtStart: "Data/hora inicial",
    dtEnd: "Data/hora final",
    latLng: "Ponto no mapa",
    range: "Raio (m)",
    leader: "Responsável",
    guard: "Contingentes",
    team: "Equipe",
    vehicle: "Veículo",
}

const initialTableColumns = {
    garrison_start: {label: "D/H inicial", visible: true},
    garrison_end: {label: "D/H final", visible: true},
    garrison_leader: {label: "Responsável", visible: true},
    garrison_name: {label: "Guarnição", visible: true},
    garrison_team: {label: "Equipe", visible: true},
    garrison_username: {label: "Contingentes", visible: true},
    garrison_intendedfunction: {label: "Função", visible: true},
    garrison_sectors: {label: "Setor", visible: true},
    initialobservation: {label: "Observação inicial", visible: false, exportOnly: true},
    finalobservation: {label: "Observação final", visible: false, exportOnly: true},
};

export default function GarrisonList() {
    const [isLoading, setIsLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [fixedTop, setFixedTop] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [tableColumns, setTableColumns] = useState(() => Object.fromEntries(
        Object.entries(initialTableColumns).map(([id, column]) => [id, {...column}])
    ));
    const [filters, setFilters] = useState(initialFilter);
    const [headerSelected, setHeaderSelected] = useState(-1);
    const printComponentRef = useRef(null);
    const buttonRef = useRef(null);
    const title = "Lista de guarnições";

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : "",
            }, filterLabels)
        );
    }, [reportData]);

    const activeFilters = () => {
        return (
            (filters.guard?.length ?? 0) > 0 ||
            (filters.team?.length ?? 0) > 0 ||
            (filters.vehicle?.length ?? 0) > 0
        );
    }

    const validateRange = () => {
        const { dtStart, dtEnd } = filters;

        if (!dtStart) {
            ReportUtils.alertErrorMessage(`O campo "Data/hora inicial" é obrigatório.`);
            return false;
        }

        if (!dtEnd) {
            ReportUtils.alertErrorMessage(`O campo "Data/hora final" é obrigatório.`);
            return false;
        }

        const maxDays = activeFilters() ? 30 : 3;
        const diffDays = differenceInDays(dtEnd, dtStart);

        if (diffDays < 0 || diffDays > maxDays) {
            if (maxDays === 3) {
                ReportUtils.alertErrorMessage(`O período máximo de pesquisa é de ${maxDays} dias.<br>Selecione Contingente, Equipe ou Veículo para pesquisar até 30 dias.`, "", true);
            } else {
                ReportUtils.alertErrorMessage(`O período máximo de pesquisa é de ${maxDays} dias.`);
            }
            return false;
         }

        return true;
    }

    const apply = async () => {
        if (!validateRange()) return;

        ReportUtils.applyLoad({
            setLoading: setIsLoading,
            clearContent: () => {
                setReportData(null)
            },
        });

        try {
            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
                latLng: filters.latLng ?? "",
                range: filters.range ?? 10,
                leader: filters.leader?.map(i => i.value) ?? [],
                guard: filters.guard?.map(i => i.value) ?? [],
                team: filters.team?.map(i => i.value) ?? [],
                vehicle: filters.vehicle?.map(i => i.value) ?? [],
            }

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd,
            }, filterLabels, true);

            const response = await GarrisonListService.apply({ ...formattedFilters, filters: auditFilters });
            setReportData(response.data);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {
            ReportUtils.alertErrorMessage("Erro ao gerar relatório.");
            console.error("Erro ao gerar relatório", error);
        } finally {
            setIsLoading(false);
        }
    }

    const clearSearch = () => {
        setFilters(initialFilter());
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }
        setCollapsed(prev => !prev);
    }

    useGlobalKey("Enter", apply);

    return (
        <SubScroll dependency={[collapsed, reportData]}>
            <div className="flex">
                <Template isLoading={isLoading}>
                    <Template.Header>
                        <div className="flex justify-between w-full">
                            <Template.Title>{title}</Template.Title>
                            <HeaderButtons
                                exportable={true}
                                exportInSheets={true}
                                printable={true}
                                headerChange={true}
                                visibilityColumns={true}
                                columns={tableColumns}
                                setColumns={setTableColumns}
                                printComponentRef={printComponentRef}
                                reportData={reportData}
                                data={reportData?.length}
                                headerSelected={headerSelected}
                                setHeaderSelected={setHeaderSelected}
                            />
                        </div>
                    </Template.Header>
                    <Template.Body className={`${collapsed ? 'p-[0]' : ""}`}>
                        <Menu
                            buttonRef={buttonRef}
                            fixedTop={fixedTop}
                            collapsed={collapsed}
                            toggleFilters={toggleFilters}
                            clearSearch={clearSearch}
                            apply={apply}
                        >
                            <Filters filters={filters} setFilters={setFilters} />
                            <AutoDatetime setFilters={setFilters} />
                        </Menu>
                    </Template.Body>
                </Template>
                <Card className={`w-full h-fit mr-2 ${!reportData || reportData?.length === 0 ? "mt-[59px]" : "mt-[61px]"} print:!mt-[15px] transform ${!reportData || reportData?.length === 0 ? "!bg-[#f3f3f4] !border-none !-translate-y-[5px]" : ""} ${collapsed ? "ml-2" : ""}`}>
                    <Card.Body>
                        <div className={`flex-1 ${!reportData || reportData?.length === 0 ? "p-[0]" : "px-[20px] py-[5px]"}`}>
                            <NotAppliedContent reportData={reportData} />
                            <NoDataContent reportData={reportData} data={reportData?.length} />
                            <Content
                                reportData={reportData}
                                data={reportData?.length}
                                entity={headerSelected}
                                title={title}
                                printComponentRef={printComponentRef}
                                setIsLoading={setIsLoading}
                                filters={filters}
                                tableColumns={tableColumns}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </div>
        </SubScroll>
    )
}